import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api, API_BASE, describeError } from '../lib/api.ts';
import { emptyLayout, isEmptyLayout, parseLayout, sanitizeLayout, type LayoutDoc } from '@jyfk/settings/layout';
import { sameLayout, withTip } from '../lib/layout-ops.ts';
import { useApp } from './app.tsx';
import { useToast } from '../components/Toast.tsx';

/**
 * 布局（每一页的板块怎么排）的读和存。
 *
 * ════════════════════════════════════════════════════════
 * 存在哪
 * ════════════════════════════════════════════════════════
 *   登录了：服务器（`/api/layout`），跟着账号走，换电脑、换手机都还在
 *   游客：  这台电脑的 localStorage。登录那一刻，如果账号里还没排过，
 *           就把他在这台电脑上排的带进账号（同游客选的语言）
 *
 * ── 什么时候存 ──
 * 拖完立刻存（攒 600 毫秒：连着挪几下只发一次）。关页面时还有没发出去的，
 * 用 keepalive 再发一次 —— 普通请求在页面关掉的那一刻会被浏览器取消。
 *
 * ── 读不到的时候**不存** ──
 * 服务器那份没读回来（网络抖了一下）时，这一页按默认排列画，用户照样能拖，
 * 但**不往服务器写**：这时候写，就是拿「默认排列 + 这一下」把他存好的那份整个盖掉。
 * 改的时候明说一句，让他刷新再调。
 */

const LOCAL_KEY = 'jyfk.layout.v1';
const SAVE_DELAY_MS = 600;

export interface LayoutApi {
  doc: LayoutDoc;
  ready: boolean;
  /** 改布局。返回改之前那一份（给「撤销」用）。改动立刻生效，存是后台的事 */
  change: (fn: (d: LayoutDoc) => LayoutDoc) => LayoutDoc;
  /** 放回某一份（撤销、导入） */
  replace: (d: LayoutDoc) => void;
}

const Ctx = createContext<LayoutApi>({
  doc: emptyLayout(),
  ready: false,
  change: () => emptyLayout(),
  replace: () => undefined
});

export const useLayout = (): LayoutApi => useContext(Ctx);

function readLocal(): LayoutDoc | null {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY);
    return raw ? sanitizeLayout(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeLocal(d: LayoutDoc): void {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(d));
  } catch {
    /* 隐身模式存储满了：这一次就只在这一页生效，不值得打扰他 */
  }
}

function dropLocal(): void {
  try { window.localStorage.removeItem(LOCAL_KEY); } catch { /* 同上 */ }
}

export function LayoutProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const app = useApp();
  const toast = useToast();
  const [doc, setDoc] = useState<LayoutDoc>(() => emptyLayout());
  const [ready, setReady] = useState(false);

  /* 回调里要读最新的值，用 ref 免得闭包拿到旧的 */
  const docRef = useRef(doc);
  docRef.current = doc;
  /** 服务器上那份读回来了没有。没读回来就不许写（见文件开头） */
  const serverKnown = useRef(false);
  const guestRef = useRef(app.guest);
  guestRef.current = app.guest;
  /** 最后一次存成功的那份。和它一样就不用再发 */
  const savedRef = useRef<LayoutDoc | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warned = useRef(false);

  const flush = useCallback(async () => {
    timer.current = null;
    if (guestRef.current || !serverKnown.current) return;
    const d = docRef.current;
    if (savedRef.current && sameLayout(savedRef.current, d)) return;
    const r = await api.put<{ ok: true }>('/layout', { layout: d });
    if (r.ok) {
      savedRef.current = d;
      warned.current = false;
      return;
    }
    /* 同一段连续失败只说一次。一直拖、一直弹会把整个屏幕盖住 */
    if (!warned.current) {
      warned.current = true;
      toast.fail('布局没存上：' + describeError(r.error) + '。这一页先按新的排，刷新会回到上次存好的样子');
    }
  }, [toast]);

  const schedule = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), SAVE_DELAY_MS);
  }, [flush]);

  const persist = useCallback((d: LayoutDoc) => {
    if (guestRef.current) {
      writeLocal(d);
      return;
    }
    if (!serverKnown.current) {
      if (!warned.current) {
        warned.current = true;
        toast.show('没读到你存好的布局，这次的调整先只在这一页生效、不会存（免得把存好的那份盖掉）。刷新一下再调', { kind: 'warn', ms: 9000 });
      }
      return;
    }
    schedule();
  }, [schedule, toast]);

  const change = useCallback((fn: (d: LayoutDoc) => LayoutDoc): LayoutDoc => {
    const before = docRef.current;
    const next = fn(before);
    /* 交出去之前过一遍严格校验：过不了的改动（比如某个容器塞超了）当场不生效，
       而不是存的时候被服务器整份拒收 */
    if (!parseLayout(next).ok || sameLayout(before, next)) return before;
    docRef.current = next;
    setDoc(next);
    persist(next);
    return before;
  }, [persist]);

  const replace = useCallback((d: LayoutDoc) => {
    const clean = sanitizeLayout(d);
    docRef.current = clean;
    setDoc(clean);
    persist(clean);
  }, [persist]);

  /* ── 读：应用启动完、或者游客刚登录完 ── */
  const whoKey = app.booting ? 'booting' : app.guest ? 'guest' : 'user:' + (app.me?.user?.id ?? '');
  useEffect(() => {
    if (whoKey === 'booting') return;
    let alive = true;
    serverKnown.current = false;
    savedRef.current = null;
    warned.current = false;

    const bumpTip = (d: LayoutDoc): LayoutDoc =>
      /* 「每一块都能挪」那条提示，前三次打开各出现一次（照原型的 dragTip） */
      !d.lock && d.tip <= 3 ? withTip(d, d.tip + 1) : d;

    if (whoKey === 'guest') {
      const d = bumpTip(readLocal() ?? emptyLayout());
      writeLocal(d);
      docRef.current = d;
      setDoc(d);
      setReady(true);
      return;
    }

    void (async () => {
      let r = await api.get<{ layout: unknown }>('/layout');
      if (!r.ok) {
        /* 抖一下就再问一次 */
        await new Promise(res => setTimeout(res, 2500));
        if (!alive) return;
        r = await api.get<{ layout: unknown }>('/layout');
      }
      if (!alive) return;
      if (!r.ok) {
        docRef.current = emptyLayout();
        setDoc(docRef.current);
        setReady(true);
        return;
      }
      serverKnown.current = true;
      let d = r.data.layout === null ? null : sanitizeLayout(r.data.layout);
      savedRef.current = d;
      /* 账号里从来没排过，而他当游客时在这台电脑上排过：带进账号 */
      const local = readLocal();
      if ((d === null || isEmptyLayout(d)) && local && !isEmptyLayout(local)) d = local;
      dropLocal();
      d = bumpTip(d ?? emptyLayout());
      docRef.current = d;
      setDoc(d);
      setReady(true);
      schedule();
    })();
    return () => { alive = false; };
  }, [whoKey, schedule]);

  /* ── 关页面时还有没发出去的：keepalive 再发一次 ── */
  useEffect(() => {
    const onHide = (): void => {
      if (timer.current === null || guestRef.current || !serverKnown.current) return;
      clearTimeout(timer.current);
      timer.current = null;
      const d = docRef.current;
      if (savedRef.current && sameLayout(savedRef.current, d)) return;
      try {
        void fetch(API_BASE + '/layout', {
          method: 'PUT', keepalive: true, credentials: 'same-origin',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ layout: d })
        });
      } catch { /* 页面都要关了，没有人能看到这条错误 */ }
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const value = useMemo<LayoutApi>(() => ({ doc, ready, change, replace }), [doc, ready, change, replace]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
